import { useEffect, useState, useCallback } from 'react';
import { Wrench, Loader2, AlertCircle, Plus, Pencil, Trash2, X, Info, Play } from 'lucide-react';
import {
  listCashServices, createCashService, updateCashService, deleteCashService,
  executeCashService,
  type CashServiceRow, type CashServiceInput,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

const svcCols = (
  onEdit: (s: CashServiceRow) => void,
  onExec: (s: CashServiceRow) => void,
  onDelete: (id: number) => void,
): DataTableColumn<CashServiceRow>[] => [
  { key: 'id', label: 'ID', width: '60px',
    cell: (s) => <span className="text-xs font-mono text-white/55">{s.id}</span> },
  { key: 'title', label: 'Título', width: '2fr',
    cell: (s) => (
      <div className="min-w-0">
        <p className="text-sm font-bold text-white truncate">{s.title || '—'}</p>
        {s.subtitle && <p className="text-[11px] text-white/45 truncate">{s.subtitle}</p>}
      </div>
    ) },
  { key: 'tipo', label: 'Tipo', width: '120px',
    cell: (s) => s.character === 1
      ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-violet-500/15 text-violet-300 border-violet-500/30">Personagem</span>
      : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-sky-500/15 text-sky-300 border-sky-500/30">Conta</span> },
  { key: 'route', label: 'Route', width: '1.4fr',
    cell: (s) => <span className="text-xs font-mono text-[#00e1d7]/90 truncate">{s.route || '—'}</span> },
  { key: 'price', label: 'Preço (Cash)', width: '120px',
    cell: (s) => <span className="text-sm font-bold text-[#00e1d7]">{(s.priceCash ?? 0).toLocaleString('pt-BR')}</span> },
  { key: 'active', label: 'Ativo', width: '80px',
    cell: (s) => s.active === 1
      ? <span className="text-[10px] font-bold text-emerald-400">SIM</span>
      : <span className="text-[10px] font-bold text-white/35">NÃO</span> },
  { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
    cell: (s) => (
      <RowActions
        actions={[
          { label: 'Editar', icon: <Pencil size={13} />, onClick: () => onEdit(s), variant: 'warning' },
          { label: 'Executar para', icon: <Play size={13} />, onClick: () => onExec(s) },
          { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => onDelete(s.id), variant: 'danger' },
        ]}
      />
    ) },
];

export default function ServicosPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<CashServiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CashServiceRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [executing, setExecuting] = useState<CashServiceRow | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listCashServices()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Deletar esse serviço?')) return;
    try {
      await deleteCashService(id);
      notify('success', 'Serviço deletado');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Wrench className="text-[#00e1d7]" size={26} />
          Serviços
        </h1>
        <p className="text-sm text-white/65 mt-1">Catálogo de serviços de Cash — o campo Route define a procedure executada.</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/65">{items.length} serviço(s)</p>
        <button onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                     btn-primary transition cursor-pointer">
          <Plus size={14} /> Novo Serviço
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<CashServiceRow>
        columns={svcCols(setEditing, setExecuting, handleDelete)}
        rows={items}
        rowKey={(s) => s.id}
        loading={loading}
        empty="Nenhum serviço cadastrado"
        mobileTitle={(s) => (
          <span className="flex items-center gap-2">{s.title || '—'} <span className="text-[10px] text-white/45 font-mono">#{s.id}</span></span>
        )}
        mobileSubtitle={(s) => `${s.character === 1 ? 'Personagem' : 'Conta'} · ${(s.priceCash ?? 0).toLocaleString('pt-BR')} Cash`}
        mobileActions={(s) => (
          <RowActions
            actions={[
              { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(s), variant: 'warning' },
              { label: 'Executar para', icon: <Play size={13} />, onClick: () => setExecuting(s) },
              { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDelete(s.id), variant: 'danger' },
            ]}
            align="left"
          />
        )}
      />

      {(editing || creating) && (
        <ServiceEditor svc={editing} onClose={() => { setEditing(null); setCreating(false); }}
                       onSaved={async () => { await fetchData(); setEditing(null); setCreating(false); }} />
      )}

      {executing && (
        <ExecServiceDialog svc={executing} onClose={() => setExecuting(null)} />
      )}
    </div>
  );
}

/* ───────────────────────────── Executar serviço (GM, grátis) ─────────────── */

function ExecServiceDialog({ svc, onClose }: { svc: CashServiceRow; onClose: () => void }) {
  const { notify } = useToast();
  const isChar = svc.character === 1;
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const exec = async () => {
    const t = parseInt(target) || 0;
    if (!t) { notify('error', isChar ? 'Informe o CharacterIdx' : 'Informe o UserNum'); return; }
    setBusy(true);
    try {
      const r = await executeCashService(svc.id, t);
      notify('success', r?.message || 'Serviço executado');
      onClose();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao executar');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Play size={16} /> Executar para</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        {/* Dados mínimos do serviço */}
        <div className="rounded-lg border border-white/[0.06] bg-black/30 p-3 mb-4">
          <p className="text-sm font-bold text-white truncate">{svc.title || `Serviço #${svc.id}`}</p>
          <p className="text-[11px] text-white/45 mt-0.5">
            {isChar
              ? <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-500/15 text-violet-300">Personagem</span>
              : <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-500/15 text-sky-300">Conta</span>}
            <span className="font-mono text-white/40"> · web_cash_service_{svc.route || '?'}</span>
          </p>
        </div>

        <Field
          label={isChar ? 'CharacterIdx do personagem' : 'UserNum da conta'}
          value={target} onChange={setTarget} numeric mono
        />
        <div className="rounded-lg bg-sky-500/[0.06] border border-sky-500/20 p-3 flex items-start gap-2 mt-3">
          <Info size={14} className="text-sky-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/70 leading-relaxed">
            Execução <b>gratuita</b> — não cobra cash nem consome crédito.
            A conta/personagem precisa estar <b>offline</b>.
          </p>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={exec} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Executar
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceEditor({ svc, onClose, onSaved }: { svc: CashServiceRow | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { notify } = useToast();
  const [title, setTitle] = useState(svc?.title || '');
  const [subtitle, setSubtitle] = useState(svc?.subtitle || '');
  const [image, setImage] = useState(svc?.image || '');
  const [priceCash, setPriceCash] = useState(String(svc?.priceCash ?? 0));
  const [route, setRoute] = useState(svc?.route || '');
  const [tipo, setTipo] = useState<'conta' | 'char'>(svc?.character === 1 ? 'char' : 'conta');
  const [active, setActive] = useState(svc?.active ?? 1);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload: CashServiceInput = {
        title: title || undefined,
        subtitle: subtitle || undefined,
        image: image || undefined,
        priceCash: parseInt(priceCash) || 0,
        active: active ? 1 : 0,
        character: tipo === 'char' ? 1 : 0,
        account: tipo === 'char' ? 0 : 1,
        route: route || undefined,
      };
      if (svc) await updateCashService(svc.id, payload);
      else await createCashService(payload);
      notify('success', svc ? 'Serviço atualizado' : 'Serviço criado');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{svc ? 'Editar' : 'Novo'} Serviço</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Título" value={title} onChange={setTitle} placeholder="ex: Trocar de Nação" />
          <Field label="Subtítulo (opcional)" value={subtitle} onChange={setSubtitle} placeholder="descrição curta" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preço (Cash)" value={priceCash} onChange={setPriceCash} numeric />
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value as 'conta' | 'char')}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
                <option value="conta">Conta</option>
                <option value="char">Personagem</option>
              </select>
            </div>
          </div>
          <Field label="Route (sufixo da procedure)" value={route} onChange={setRoute} mono placeholder="ex: nation" />
          <Field label="Imagem (URL)" value={image} onChange={setImage} mono />
          <label className="flex items-center gap-2 text-sm text-white/85">
            <input type="checkbox" checked={!!active} onChange={(e) => setActive(e.target.checked ? 1 : 0)} />
            Serviço ativo (visível pra players)
          </label>
          <div className="rounded-lg bg-sky-500/[0.06] border border-sky-500/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-sky-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-white/70 leading-relaxed">
              Ao executar um serviço, o sistema roda no banco a procedure{' '}
              <code className="text-sky-300 font-mono">web_cash_service_{route || '{route}'}</code>{' '}
              conforme o campo <b>Route</b>.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={save} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, numeric, mono, placeholder }:
  { label: string; value: string; onChange: (v: string) => void; numeric?: boolean; mono?: boolean; placeholder?: string }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input
        type="text" inputMode={numeric ? 'numeric' : undefined}
        value={value} placeholder={placeholder}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
